import * as React from 'react';
import {useMemo} from 'react';
import {YmTermInput} from "@/component/input/YmTermInput";
import {FtDate} from "@/common/util/FtDate";
import {Link, useSearchParams} from "react-router-dom";
import {NumberTable} from "@/component/style/NumberTable";
import {useAsyncEffect} from "@/hook/useAsyncEffect";
import {Money} from "@/component/style/Money";
import {useAccountStore} from "@/store/useAccountStore";
import {BillingAccountType, TransAccount} from "@/common/dto/TransAccount";
import {accountTypeNames, AccountTypes} from "@/constatants";
import {LoadableView} from "@/component/view/LoadableView";
import {ApiClient} from "@/api/ApiClient";

interface Data {
    accounts: Record<BillingAccountType, TransAccount[]>;
    monthlyData: Record<string, number>[];
    monthlyTypeSum: Record<string, number>[];
}

export function MonthlySummaryPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const ymFrom = searchParams.get("ymFrom") || new FtDate().setMonth(1).ym();
    const ymTo = searchParams.get("ymTo") || new FtDate().ym();
    const [data, setData] = React.useState<Data | undefined>();

    const accountStore = useAccountStore();

    const months = useMemo(() => {
        const months: string[] = [];
        let current = ymFrom;
        while (current <= ymTo) {
            months.push(current);
            current = new FtDate(current).addMonth(1).ym();
        }
        return months;
    }, [ymFrom, ymTo]);

    const error = useAsyncEffect(async () => {
        const accounts: Record<BillingAccountType, TransAccount[]> = {} as any;
        const monthlyData: Record<number, number>[] = [];
        const monthlyTypeSum: Record<string, number>[] = [];

        for (const accountType of AccountTypes) {
            accounts[accountType] = [];
        }

        const accountIds = new Set<number>();
        for (const month of months) {
            const begin = new FtDate(month).ymd();
            const end = new FtDate(month).lastDayOfMonth().ymd();

            const monthData: Record<number, number> = {};
            const monthSummary = await ApiClient.instance.getBalances(begin, end);
            const typeSum: Record<string, number> = {};

            for (const accountId in monthSummary) {
                const account = accountStore.getAccountById(Number(accountId));
                if (!account) {
                    console.error(`Account ${accountId} not found!`);
                    continue;
                }
                const sum = monthSummary[accountId];
                monthData[accountId] = sum.inSum - sum.outSum;
                accountIds.add(Number(accountId));
                typeSum[account.type] = (typeSum[account.type] ?? 0) + monthData[accountId];
            }
            monthlyTypeSum.push(typeSum);
            monthlyData.push(monthData);
        }

        for (const accountId of accountIds) {
            const acc = accountStore.getAccountById(accountId);
            if (!acc) {
                console.error(`Account ${accountId} not found`);
                continue;
            }
            accounts[acc.type]?.push(acc);
        }
        for (const accountType of AccountTypes) {
            accounts[accountType]?.sort((a, b) => a.name.localeCompare(b.name));
        }

        setData({monthlyData, accounts, monthlyTypeSum});
    }, [months]);

    return <div>
        <YmTermInput ymFrom={ymFrom} ymTo={ymTo} mode='YEAR' recent={4} onChange={(ymFrom, ymTo) => {
            setSearchParams({ymFrom: ymFrom, ymTo: ymTo});
        }}/>

        <LoadableView data={data} error={error} component={(data) => (
            <div style={{width: '100%', maxWidth: '100%', overflowX: 'auto', marginTop: 16}}>
                <NumberTable className="small">
                    <thead>
                    <tr>
                        <th></th>
                        {months.map(ym => <th key={ym} style={{whiteSpace: 'nowrap'}}>{ym}</th>)}
                        <th>합계</th>
                    </tr>
                    </thead>
                    <tbody>
                        <AccountTypeRows data={data} months={months} accountType='INC' sign={-1}/>
                        <AccountTypeRows data={data} months={months} accountType='EXP' sign={1}/>
                        <IncExpRow data={data}/>
                        <AccountTypeRows data={data} months={months} accountType='BANK' sign={1}/>
                        <AccountTypeRows data={data} months={months} accountType='EXT' sign={-1}/>
                        <AccountTypeRows data={data} months={months} accountType='SAVE' sign={1}/>
                        <AccountTypeRows data={data} months={months} accountType='INVESTMENT' sign={1}/>
                        <SumRow title="계좌 합계" data={data} accountTypes={['BANK', 'EXT', 'SAVE', 'INVESTMENT']}/>
                    </tbody>
                </NumberTable>
            </div>
        )}/>
    </div>;
}

function AccountTypeRows({data, months, accountType, sign}: { data: Data, months: string[], accountType: BillingAccountType, sign: number}) {
    function dateParams(index: number) {
        const month = months[index];
        const begin = new FtDate(month).ymd();
        const end = new FtDate(month).lastDayOfMonth().ymd();
        return `ymdFrom=${begin}&ymdTo=${end}`;
    }

    return <>
        {data.accounts[accountType]?.map(account => (
            <tr key={account.id}>
                <td style={{textAlign: 'right', whiteSpace: 'nowrap'}}>
                    {account.name}
                </td>
                {data.monthlyData.map((monthSummary, index) => (
                    <td key={index} style={{textAlign: 'right'}}>
                        <Link to={`/accounts/${account.id}/transactions?${dateParams(index)}`}><Money amount={sign * (monthSummary[account.id] ?? 0)}/></Link>
                    </td>
                ))}
                <td style={{textAlign: 'right'}}>
                    <Money amount={sign * data.monthlyData.reduce((sum, monthSummary) => sum + (monthSummary[account.id] ?? 0), 0)}/>
                </td>
            </tr>
        ))}

        <tr className="sub-total">
            <td style={{textAlign: 'right', whiteSpace: 'nowrap', fontWeight: 'bold'}}>{accountTypeNames[accountType]} 합계</td>

            {data.monthlyTypeSum.map((typeSum, index) => (
                <td key={index} style={{textAlign: 'right'}}>
                    <Money amount={sign * (typeSum[accountType] ?? 0)}/>
                </td>
            ))}

            <td style={{textAlign: 'right'}}>
                <Money amount={sign * data.monthlyTypeSum.reduce((sum, typeSum) => sum + (typeSum[accountType] ?? 0), 0)}/>
            </td>
        </tr>
    </>
}

function IncExpRow({data}: { data: Data}) {
    return <tr className="total">
        <td style={{textAlign: 'right', whiteSpace: 'nowrap', fontWeight: 'bold'}}>수입-지출</td>
        {data.monthlyTypeSum.map((typeSum, index) => (
            <td key={index} style={{textAlign: 'right'}}>
                <Money amount={-(typeSum['INC'] ?? 0) - (typeSum['EXP'] ?? 0)}/>
            </td>
        ))}
        <td style={{textAlign: 'right'}}>
            <Money amount={data.monthlyTypeSum.reduce((sum, typeSum) => sum - (typeSum['INC'] ?? 0) - (typeSum['EXP'] ?? 0), 0)}/>
        </td>
    </tr>
}

function SumRow({data, accountTypes, title}: { data: Data, accountTypes: BillingAccountType[], title: string}) {
    const monthly = useMemo(() => {
        const monthly: number[] = [];
        for (let i = 0; i < data.monthlyTypeSum.length; i++) {
            let s = 0;
            for (const accountType of accountTypes) {
                s += data.monthlyTypeSum[i][accountType] ?? 0;
            }
            monthly.push(s);
        }
        return monthly;
    }, [data, accountTypes]);

    const total = monthly.reduce((s, v) => s + v, 0);

    return <tr className="total">
        <td style={{textAlign: 'right', whiteSpace: 'nowrap', fontWeight: 'bold'}}>{title}</td>
        {monthly.map((s, index) => (
            <td key={index} style={{textAlign: 'right'}}>
                <Money amount={s}/>
            </td>
        ))}
        <td style={{textAlign: 'right'}}>
            <Money amount={total}/>
        </td>
    </tr>
}